/**
 * Générateur — Automatisme : reconnaitre des fractions égales :
 * 2/3 = …/15 ; 4/7 = …/14.
 * Attendu ID : 1051 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8). Cadrage rédigé
 * par le sous-agent programme-maths, validé par le prof le 2026-09-08.
 *
 * Le geste : devant a/b = …/c avec c dans la table de b, écrire a × (c ÷ b)
 * sans hésiter ; devant une égalité écrite, dire si le haut et le bas ont
 * été multipliés par LE MÊME nombre. Deux faits de table, aucun calcul posé.
 * Sens direct seulement (agrandir) : simplifier (15/35 → 3/7) est un
 * automatisme de 4e ; le trou au dénominateur est la forme de 6e (gen_627).
 *
 * Plan de cartes validé, trois cartes :
 *   - Série flash                            (4 égalités à trou, saisie,
 *                                             un trou entier chacune)
 *   - Le même nombre en haut et en bas       (QCM 4 choix, Vrai/Faux
 *                                             3 affirmations, Relier 3 + 1)
 *   - Le même point                          (trois droites alignées,
 *                                             a/b = ⬚/c₁ = ⬚/c₂, saisie)
 *
 * ── Domaine (tout en entiers, tout dans les tables) ──────────────────────
 * Triplet (a, b, k) : a de 2 à 9, b de 2 à 10, a et b premiers entre eux,
 * a ≠ b ; k de 2 à 10 ; c = b × k ≤ 100 ; numérateur cherché n = a × k ≤ 90 ;
 * a ≠ k (sinon « coefficient » et « recopie » se confondent). a ≥ 2 : avec
 * a = 1 le numérateur cherché EST le coefficient. Fraction de départ
 * inférieure à 1 trois fois sur quatre. b = 10, k = 10 gardé (centièmes,
 * lien avec 1048). Un seul trou, toujours au numérateur.
 * TRIPLET SACRIFIÉ : (3, 4, 2), 3/4 = 6/8, l'exemple du coup de pouce : ne
 * sort d'aucune carte, ni comme égalité, ni comme paire du Relier, ni comme
 * affirmation vraie (auto-testé par cdp1051.self.test.js).
 *
 * ── Distracteurs (familles nommées, cohérentes entre les cartes) ─────────
 * Pour a/b = …/c, k = c ÷ b, clé n = a × k :
 *   addition          : a + (c − b), il ajoute au haut ce qu'on a ajouté au
 *                       bas (2/3 = 14/15) — L'ERREUR PRINCIPALE, toujours
 *                       présente au QCM, le leurre du Relier ;
 *   coefficient       : k, il répond le multiplicateur au lieu de l'appliquer ;
 *   recopie           : a, seul le dénominateur a changé ;
 *   voisin_meme_table : a × (k ± 1), une erreur de table ;
 *   deux_coefficients : Vrai/Faux seulement, haut × k1, bas × k2, k1 ≠ k2 ;
 *   inversion         : Vrai/Faux seulement, minoritaire, b·k/a·k.
 * Chaque distracteur est vérifié différent de la clé et des autres EN
 * VALEUR ; collision → nouveau tirage.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, tex,
} from '../../../utils';

const ATTENDU_ID = 1051;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_MEME = 'Le même nombre en haut et en bas';
const L_POINT = 'Le même point';

// ─── Le domaine ───────────────────────────────────────────────────────────

const A_MIN = 2;
const A_MAX = 9;
const B_MIN = 2;
const B_MAX = 10;
const K_MIN = 2;
const K_MAX = 10;
const C_MAX = 100;
const N_MAX = 90;

/** Le triplet de l'exemple du coup de pouce, sacrifié : 3/4 = 6/8. */
export const TRIPLET_SACRIFIE = { a: 3, b: 4, k: 2 };

const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));
const estSacrifie = (a, b, k) => a === TRIPLET_SACRIFIE.a && b === TRIPLET_SACRIFIE.b && k === TRIPLET_SACRIFIE.k;

const valide = (a, b, k) => a >= A_MIN && a <= A_MAX && b >= B_MIN && b <= B_MAX && k >= K_MIN && k <= K_MAX
    && gcd(a, b) === 1 && a !== b && a !== k && b * k <= C_MAX && a * k <= N_MAX && !estSacrifie(a, b, k);

const triplet = (a, b, k) => {
    if (!valide(a, b, k)) throw new Error(`gen_1051 : triplet hors domaine ${a}/${b} × ${k}`);
    return { a, b, k, c: b * k, n: a * k };
};

/** Replis déterministes vérifiés, dans l'ordre : a < b, a > b, trou à gauche possible pour tous. */
const REPLIS = [[2, 3, 5], [7, 4, 3], [4, 7, 2], [5, 6, 7]];

/**
 * Un triplet au hasard. `superieur` : true → a > b, false → a < b, null →
 * a < b trois fois sur quatre. `kMin`, `exclusK` : pour la Série flash.
 */
const tirerTriplet = ({ superieur = null, kMin = K_MIN, exclusK = [] } = {}) => {
    const sup = superieur === null ? randomChoice([false, false, false, true]) : superieur;
    for (let essai = 0; essai < 200; essai++) {
        const a = randInt(A_MIN, A_MAX);
        const b = randInt(B_MIN, B_MAX);
        const k = randInt(kMin, K_MAX);
        if ((sup && a < b) || (!sup && a > b) || exclusK.includes(k) || !valide(a, b, k)) continue;
        return triplet(a, b, k);
    }
    const [a, b, k] = REPLIS.find(([x, y, z]) => (sup ? x > y : x < y) && z >= kMin && !exclusK.includes(z)) || REPLIS[0];
    return triplet(a, b, k);
};

/** Égalité rationnelle en croix. */
const memeValeur = (p, q, r, s) => p * s === r * q;

// ─── Écritures ────────────────────────────────────────────────────────────

const fracTex = (n, d) => `\\dfrac{${n}}{${d}}`;
/** Fraction dont le numérateur est un trou (étai pour garder la barre visible). */
const fracTrou = (n, d) => `\\dfrac{{{${n}}}\\rule[-0.55em]{0pt}{0.55em}}{${d}}`;
/** L'égalité affichée, trou à droite (a/b = ⬚/c) ou à gauche (⬚/c = a/b). */
const egaliteTrou = (t, gauche) => (gauche
    ? `${fracTrou(t.n, t.c)} = ${fracTex(t.a, t.b)}`
    : `${fracTex(t.a, t.b)} = ${fracTrou(t.n, t.c)}`);
const egalitePoints = (t, gauche) => (gauche
    ? `\\dfrac{\\ldots}{${t.c}} = ${fracTex(t.a, t.b)}`
    : `${fracTex(t.a, t.b)} = \\dfrac{\\ldots}{${t.c}}`);

// ─── Distracteurs ─────────────────────────────────────────────────────────

const EXPLICATIONS = {
    addition: 'On ne rajoute pas le même nombre en haut et en bas : on multiplie le haut et le bas par le même nombre.',
    coefficient: 'Le nombre par lequel on multiplie ne s\'écrit pas en haut : on l\'applique au numérateur.',
    recopie: 'Si le bas a été multiplié, le haut doit l\'être aussi, par le même nombre.',
    voisin_meme_table: 'Le haut se multiplie par le même nombre que le bas : je retrouve ce nombre dans la table du dénominateur.',
    deux_coefficients: 'Haut et bas ont été multipliés par deux nombres différents : ce n\'est pas le même nombre.',
    inversion: 'Le haut reste en haut et le bas reste en bas.',
    regle: 'Deux fractions sont égales quand le haut et le bas ont été multipliés par le même nombre.',
};

/**
 * QCM : quatre choix entiers, addition toujours, coefficient, puis recopie ou
 * voisin de table en rotation. null si deux valeurs coïncident.
 *
 * BIAIS DE POSITION ASSUMÉ (revue prof 2026-09-08, mesuré sur 5000 tirages) :
 * la clé n'est jamais la plus petite des quatre valeurs et elle est la
 * deuxième plus grande 63 % du temps. C'est la nature des erreurs : recopie
 * (a) et coefficient (k) sont toujours plus petits que a × k, l'addition est
 * plus grande dès que a < b. Les distracteurs sont les bonnes erreurs, on
 * n'en invente pas pour rééquilibrer. Ne pas rouvrir.
 */
const distracteursQcm = (t) => {
    const addition = { valeur: t.a + (t.c - t.b), code: 'addition' };
    const coefficient = { valeur: t.k, code: 'coefficient' };
    const voisin = { valeur: t.a * (t.k + randomChoice([-1, 1])), code: 'voisin_meme_table' };
    const recopie = { valeur: t.a, code: 'recopie' };
    const faux = [addition, coefficient, randomChoice([recopie, voisin])];
    const valeurs = [t.n, ...faux.map((f) => f.valeur)];
    if (new Set(valeurs).size !== 4 || valeurs.some((v) => v < 1)) return null;
    return faux;
};

// ─── Carte 1 : Série flash (4 égalités à trou, saisie) ────────────────────

const CONSIGNE_FLASH = '<p><b>Complète chaque égalité.</b></p>';

/**
 * Quatre égalités, DEUX PAR LIGNE KaTeX (une ligne KaTeX ne se replie
 * jamais ; deux égalités à une case tiennent sur téléphone, cf. 1050).
 */
const phraseFlash = (items) => {
    const lignes = [];
    for (let i = 0; i < items.length; i += 2) {
        lignes.push(`$${items.slice(i, i + 2).map((it) => egaliteTrou(it, it.gauche)).join(' \\quad ; \\quad ')}$`);
    }
    return lignes.join('\n');
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        // quatre coefficients distincts dont un ≥ 5, une fraction > 1, un trou à gauche
        const items = [];
        const grand = tirerTriplet({ kMin: 5 });
        items.push(grand);
        const sup = tirerTriplet({ superieur: true, exclusK: items.map((it) => it.k) });
        items.push(sup);
        for (let j = 0; j < 2; j++) {
            items.push(tirerTriplet({ superieur: false, exclusK: items.map((it) => it.k) }));
        }
        if (new Set(items.map((it) => it.k)).size < 4) continue;
        if (new Set(items.map((it) => `${it.a}/${it.b}`)).size < 4) continue;
        const ordre = shuffle(items);
        const iGauche = randInt(0, 3);
        const finaux = ordre.map((it, i) => ({ ...it, gauche: i === iGauche }));
        const payload = makeTexteTrou(phraseFlash(finaux), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
        payload._meta.params = { items: finaux.map(({ a, b, k, gauche }) => ({ a, b, k, gauche })) };
        return payload;
    }
    // Repli déterministe : 2/3 = ⬚/15 (k 5), 7/4 = ⬚/12 (k 3), ⬚/14 = 4/7 (k 2, à gauche), 5/6 = ⬚/42 (k 7).
    const finaux = [
        { ...triplet(2, 3, 5), gauche: false }, { ...triplet(7, 4, 3), gauche: false },
        { ...triplet(4, 7, 2), gauche: true }, { ...triplet(5, 6, 7), gauche: false },
    ];
    const payload = makeTexteTrou(phraseFlash(finaux), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items: finaux.map(({ a, b, k, gauche }) => ({ a, b, k, gauche })) };
    return payload;
};

// ─── Carte 2, format A : QCM, quatre choix ────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const t = tirerTriplet();
        const faux = distracteursQcm(t);
        if (!faux) continue;
        const gauche = randomChoice([false, false, true]);
        const bon = String(t.n);
        const texte = `<p><b>Pour compléter ${tex(egalitePoints(t, gauche))}, quel nombre écris-tu ?</b></p>`;
        const payload = makeQcm([bon, ...faux.map((f) => String(f.valeur))], bon, texte, opts(L_MEME));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...t, gauche, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe vérifié : 2/3 = …/15 → 10 ; addition 14, coefficient 5, recopie 2.
    const t = triplet(2, 3, 5);
    const payload = makeQcm(['10', '14', '5', '2'], '10',
        `<p><b>Pour compléter ${tex(egalitePoints(t, false))}, quel nombre écris-tu ?</b></p>`, opts(L_MEME));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...t, gauche: false, distracteurs: [{ code: 'addition' }, { code: 'coefficient' }, { code: 'recopie' }] };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 affirmations) ────────────────────

/**
 * Une affirmation a/b = n/d, vraie (n = a·k, d = b·k) ou fausse selon la
 * famille ; la fausse est vérifiée fausse en croix. null si impossible.
 */
const affirmation = (t, famille) => {
    let n;
    let d = t.c;
    if (famille === null) { n = t.n; }
    else if (famille === 'addition') n = t.a + (t.c - t.b);
    else if (famille === 'recopie') n = t.a;
    else if (famille === 'inversion') { n = t.c; d = t.n; }
    else {
        // deux_coefficients : haut × k1, bas × k (k1 ≠ k), dans le domaine, et
        // la fausse reste DU MÊME CÔTÉ DE 1 que la vraie (revue prof
        // 2026-09-08 : 5/7 = 15/10 se voyait faux sans comparer les deux
        // multiplications) : a·k1 < b·k si a < b, a·k1 > b·k si a > b.
        const memeCote = (x) => ((t.a < t.b) === (t.a * x < t.b * t.k));
        const k1 = randomChoice([2, 3, 4, 5, 6, 7, 8, 9, 10].filter((x) => x !== t.k && t.a * x <= N_MAX && memeCote(x)));
        if (!k1) return null;
        n = t.a * k1;
    }
    if (famille !== null && memeValeur(t.a, t.b, n, d)) return null;
    return { ligne: tex(`${fracTex(t.a, t.b)} = ${fracTex(n, d)}`), code: famille, n, d };
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const ts = [tirerTriplet(), tirerTriplet(), tirerTriplet()];
        if (new Set(ts.map((t) => `${t.a}/${t.b}`)).size < 3) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        // au moins une fausse « deux coefficients » par tableau (cadrage) : la
        // seule fausse quand il y a deux vraies, l'une des deux sinon, l'autre
        // en rotation (revue prof 2026-09-08 : un tableau sur quatre n'en avait
        // pas quand la famille de l'unique fausse était tirée au hasard)
        const famillesFausses = nbVraies === 2
            ? ['deux_coefficients']
            : shuffle(['deux_coefficients', randomChoice(['addition', 'addition', 'recopie', 'inversion'])]);
        let iFaux = 0;
        const lignes = [];
        const codes = [];
        let ok = true;
        ts.forEach((t, i) => {
            const fam = verites[i] ? null : famillesFausses[iFaux++];
            const aff = affirmation(t, fam);
            if (!aff) { ok = false; return; }
            lignes.push(aff.ligne);
            if (aff.code) codes.push(aff.code);
        });
        if (!ok || new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_MEME));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { triplets: ts.map(({ a, b, k }) => ({ a, b, k })), verites, distracteurs: codes.map((code) => ({ code })) };
        return payload;
    }
    // Repli déterministe : 2/3 = 10/15 vrai ; 4/7 = 8/21 faux (deux coefficients) ; 5/6 = 7/8 faux (addition).
    const ts = [triplet(2, 3, 5), triplet(4, 7, 3), triplet(5, 6, 2)];
    const lignes = [
        tex(`${fracTex(2, 3)} = ${fracTex(10, 15)}`),
        tex(`${fracTex(4, 7)} = ${fracTex(8, 21)}`),
        tex(`${fracTex(5, 6)} = ${fracTex(7, 12)}`),
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_MEME));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { triplets: ts.map(({ a, b, k }) => ({ a, b, k })), verites: [true, false, false], distracteurs: [{ code: 'deux_coefficients' }, { code: 'addition' }] };
    return payload;
};

// ─── Carte 2, format C : Relier 4 fractions ↔ 4 agrandies + 1 leurre ──────

const genRelier = () => {
    for (let essai = 0; essai < 80; essai++) {
        // TROIS paires + un leurre (revue prof 2026-09-08 : quatre paires,
        // c'était trop chargé), soit quatre fractions à droite.
        const ts = [tirerTriplet(), tirerTriplet(), tirerTriplet()];
        // sources deux à deux distinctes en valeur (fractions irréductibles → distinctes en écriture suffit)
        if (new Set(ts.map((t) => `${t.a}/${t.b}`)).size < 3) continue;
        // leurre par addition sur une des paires, distinct de toutes les valeurs
        const cible = randomChoice(ts);
        const leurre = { n: cible.a + (cible.c - cible.b), d: cible.c };
        if (ts.some((t) => memeValeur(t.a, t.b, leurre.n, leurre.d))) continue;
        const pairs = ts.map((t) => ({ source: `$${fracTex(t.a, t.b)}$`, target: `$${fracTex(t.n, t.c)}$` }));
        const targets = [...pairs.map((p) => p.target), `$${fracTex(leurre.n, leurre.d)}$`];
        if (new Set(targets).size < 4 || new Set(pairs.map((p) => p.source)).size < 3) continue;
        const payload = makeRelier(shuffle(pairs),
            '<p><b>Relie chaque fraction à une fraction égale.</b> Une fraction est en trop.</p>',
            { ...opts(L_MEME), cibles_leurres: [{ target: `$${fracTex(leurre.n, leurre.d)}$`, code: 'addition' }] });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { triplets: ts.map(({ a, b, k }) => ({ a, b, k })), leurre, distracteurs: [{ code: 'addition' }] };
        return payload;
    }
    // Repli déterministe vérifié : 2/3 → 10/15, 7/4 → 21/12, 4/7 → 8/14 ; leurre 14/15 (addition sur 2/3).
    const ts = [triplet(2, 3, 5), triplet(7, 4, 3), triplet(4, 7, 2)];
    const pairs = ts.map((t) => ({ source: `$${fracTex(t.a, t.b)}$`, target: `$${fracTex(t.n, t.c)}$` }));
    const payload = makeRelier(pairs,
        '<p><b>Relie chaque fraction à une fraction égale.</b> Une fraction est en trop.</p>',
        { ...opts(L_MEME), cibles_leurres: [{ target: `$${fracTex(14, 15)}$`, code: 'addition' }] });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { triplets: ts.map(({ a, b, k }) => ({ a, b, k })), leurre: { n: 14, d: 15 }, distracteurs: [{ code: 'addition' }] };
    return payload;
};

// ─── Carte 3 : Le même point (trois droites alignées, saisie) ─────────────
//
// Idée prof 2026-09-08, reprise de la Série flash de 1050 en la FORÇANT sur
// les fractions égales : le même point A sur trois droites de même longueur
// (le composant trace toutes les droites sur la même largeur, les entiers
// et le point tombent à la verticale), graduées en b-ièmes puis en b·k₁ et
// b·k₂-ièmes. Sur la première, la fraction a/b est affichée sous A ; sur les
// deux autres, l'élève compte (le geste de 1050) et écrit a/b = ⬚/c₁ = ⬚/c₂.
// Pas de distracteur (saisie) : l'erreur d'addition se voit d'elle-même,
// 14/15 n'est pas sur le point.

const COULEUR_POINT = '#fb923c';
const GRAD_MAX_UNITE = 20; // graduations par unité, droite de 0 à 1
const GRAD_MAX_DEUX = 16; //  droite de 0 à 2 (a > b), ≤ 33 traits

/** Une droite : pas 1/den, A à a/b, fraction affichée sous A seulement si `afficher`. */
const droitePoint = (a, b, den, end, afficher) => ({
    type: 'numberline',
    config: {
        start: 0, end, step: 1 / den,
        reperes: end === 2 ? [1] : [],
        points: [{ value: a / b, label: 'A', color: COULEUR_POINT, showValue: afficher, ...(afficher ? { displayFraction: { num: a, den: b } } : {}) }],
        lineType: 'half', showArrow: true, showStart: true, showEnd: true,
    },
});

const tirerPoint = () => {
    for (let essai = 0; essai < 200; essai++) {
        const b = randInt(2, 5);
        const sup = randomChoice([false, false, false, true]);
        const a = sup ? randInt(b + 1, 2 * b - 1) : randInt(1, b - 1);
        if (gcd(a, b) !== 1) continue;
        const end = sup ? 2 : 1;
        const gradMax = sup ? GRAD_MAX_DEUX : GRAD_MAX_UNITE;
        const ks = shuffle([2, 3, 4, 5].filter((k) => b * k <= gradMax && !estSacrifie(a, b, k))).slice(0, 2);
        if (ks.length < 2) continue;
        return { a, b, end, ks: ks.sort((x, y) => x - y) };
    }
    return { a: 2, b: 3, end: 1, ks: [2, 5] };
};

const genPoint = () => {
    const p = tirerPoint();
    const droites = [droitePoint(p.a, p.b, p.b, p.end, true), ...p.ks.map((k) => droitePoint(p.a, p.b, p.b * k, p.end, false))];
    const texte = '<p><b>Le point A est le même sur les trois droites.</b> Complète.</p>{{element:0}}{{element:1}}{{element:2}}';
    const phrase = `$${fracTex(p.a, p.b)} = ${p.ks.map((k) => fracTrou(p.a * k, p.b * k)).join(' = ')}$`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_POINT), mode: 'saisie', strict: true, elements_additionnels: { version: 1, elements: droites },
    });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { ...p };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix entiers ; chaque distracteur est vérifié différent de la clé et des autres EN VALEUR ; collision → nouveau tirage complet',
        'a de 2 à 9, b de 2 à 10 premiers entre eux, k de 2 à 10, c = b·k ≤ 100, n = a·k ≤ 90, a ≠ k ; jamais le triplet sacrifié (3, 4, 2)',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    meme_qcm_unique: {
        support: '« Pour compléter a/b = …/c (ou …/c = a/b une fois sur trois), quel nombre écris-tu ? »',
        fixes: {
            addition: 'a + (c − b), TOUJOURS : il ajoute au haut ce qu\'on a ajouté au bas (l\'erreur principale)',
            coefficient: 'k, TOUJOURS : il répond le multiplicateur au lieu de l\'appliquer',
        },
        rotation: {
            recopie: 'a, seul le dénominateur a changé',
            voisin_meme_table: 'a × (k ± 1), une erreur de table',
        },
    },
    meme_tableau_vf: 'trois affirmations a/b = n/d, 1 ou 2 vraies ; une fausse « deux coefficients » par tableau, l\'autre parmi addition (deux fois sur quatre), recopie, inversion',
    meme_relier: '3 fractions irréductibles ↔ 3 agrandies + 1 leurre par addition, toutes les valeurs distinctes en croix',
    point_texte_trou_saisie: 'trois droites alignées (b, b·k₁, b·k₂-ièmes), A affiché en a/b sur la première, a/b = ⬚/c₁ = ⬚/c₂ ; sans distracteur',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    meme_qcm_unique: genQcm,
    meme_tableau_vf: genVf,
    meme_relier: genRelier,
    point_texte_trou_saisie: genPoint,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'meme', label: L_MEME, formatsList: [
        { key: 'meme_qcm_unique', label: 'QCM' },
        { key: 'meme_tableau_vf', label: 'Vrai/Faux' },
        { key: 'meme_relier', label: 'Relier' },
    ] },
    { id: 'point', label: L_POINT, formatsList: [{ key: 'point_texte_trou_saisie', label: 'Saisie trous' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
